import { useMutation } from '@apollo/client';
import FormField from '@redirector/front/components/common/FormField';
import InlineError from '@redirector/front/components/common/InlineError';
import Modal from '@redirector/front/components/common/Modal';
import { useAuth, useCurrentUser } from '@redirector/front/context/useAuth';
import { CHANGE_PASSWORD,UPDATE_USER } from '@redirector/front/gql/operations';
import { isValidEmail, toUIError, type UIError } from '@redirector/front/lib/errors';
import { INPUT_BASE, INPUT_ERROR, SUBMIT_BUTTON } from '@redirector/front/lib/formStyles';
import { Key, Loader2, ShieldCheck,User as UserIcon } from 'lucide-react';
import { useState } from 'react';

interface ProfileModalProps {
  onClose: () => void;
}

export default function ProfileModal({ onClose }: ProfileModalProps) {
  const user = useCurrentUser();
  const { isRootAdmin, refreshUser } = useAuth();

  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email ?? '');
  const [avatar, setAvatar] = useState(user.avatar ?? '');

  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [profileSuccess, setProfileSuccess] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState<UIError | null>(null);
  const [profileError, setProfileError] = useState<UIError | null>(null);

  const [updateUser, { loading: updatingProfile }] = useMutation(UPDATE_USER, {
    onCompleted: () => {
      setProfileSuccess(true);
      refreshUser();
    },
    onError: (err) => setProfileError(toUIError(err)),
  });

  const [changePassword, { loading: changingPassword }] = useMutation(CHANGE_PASSWORD, {
    onCompleted: () => {
      setPasswordSuccess(true);
      setPasswordError(null);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (err) => setPasswordError(toUIError(err)),
  });

  const trimmedEmail = email.trim();
  const emailValid = trimmedEmail === '' || isValidEmail(trimmedEmail);
  const loading = updatingProfile || changingPassword;

  const handleProfileSave = (e: React.FormEvent) => {
    e.preventDefault();
    setProfileSuccess(false);
    setProfileError(null);
    if (!emailValid) {
      setProfileError({ message: 'Please enter a valid email address' });
      return;
    }
    updateUser({
      variables: {
        input: {
          id: user.id,
          name: name || user.name,
          // `email` is a read-only convenience scalar on User. Updating the
          // primary contact must use the schema's ordered emails input.
          emails: trimmedEmail === '' ? [] : [{ value: trimmedEmail }],
          // '' clears the avatar.
          avatar,
        },
      },
    });
  };

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordSuccess(false);
    setPasswordError(null);

    if (newPassword !== confirmPassword) {
      setPasswordError({ message: 'New passwords do not match' });
      return;
    }
    if (newPassword.length < 4) {
      setPasswordError({ message: 'Password must be at least 4 characters' });
      return;
    }

    changePassword({
      variables: {
        input: {
          userId: user.id,
          currentPassword,
          newPassword,
        },
      },
    });
  };

  return (
    <Modal
      title="Your Profile"
      icon={<UserIcon className="w-5 h-5 text-blue-500" />}
      onClose={onClose}
      loading={loading}
      showFooter={false}
    >
      {/* Identity summary */}
      <div className="flex items-center gap-3">
        {user.avatar ? (
          <img src={user.avatar} alt="" className="w-12 h-12 rounded-full" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-[var(--color-surface-hover)] flex items-center justify-center">
            <UserIcon className="w-6 h-6 text-[var(--color-text-muted)]" />
          </div>
        )}
        <div>
          <div className="text-white font-medium flex items-center gap-2">
            {user.name}
            {isRootAdmin && (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 bg-amber-500/20 text-amber-300 rounded"
                title="Super-admin"
              >
                <ShieldCheck className="w-3 h-3" />
                ROOT
              </span>
            )}
          </div>
          <div className="text-sm text-[var(--color-text-muted)]">@{user.username}</div>
        </div>
      </div>

      {/* Memberships */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-2">
          Your Memberships
        </h3>
        {user.memberships.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)] italic">
            You don't belong to any group yet.
          </p>
        ) : (
          <div className="space-y-2">
            {user.memberships.map((m) => (
              <div
                key={m.groupId}
                className="flex items-start justify-between gap-3 p-3 bg-[var(--color-background)] rounded-lg border border-[var(--color-border)]"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-white flex items-center gap-2">
                    {m.groupName}
                    {m.isDisabled && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 bg-red-500/20 text-red-300 rounded">
                        SUSPENDED
                      </span>
                    )}
                  </div>
                  {/* The API rejects memberships without roles and in-use role deletion. */}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {m.roles.map((r) => (
                      <span
                        key={r.id}
                        title={r.description}
                        className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-blue-500/15 text-blue-300 ring-1 ring-blue-500/20"
                      >
                        {r.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Editable profile */}
      <form onSubmit={handleProfileSave} className="space-y-4 border-t border-[var(--color-border)] pt-5">
        <FormField label="Display Name" htmlFor="pm-name">
          <input
            id="pm-name"
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setProfileSuccess(false); }}
            placeholder="Your name"
            className={INPUT_BASE}
          />
        </FormField>

        <FormField label="Email" htmlFor="pm-email" error={emailValid ? null : 'Please enter a valid email address'}>
          <input
            id="pm-email"
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setProfileSuccess(false); }}
            placeholder="you@example.com"
            aria-invalid={!emailValid}
            className={emailValid ? INPUT_BASE : INPUT_ERROR}
          />
        </FormField>

        <FormField label="Avatar URL" htmlFor="pm-avatar">
          <input
            id="pm-avatar"
            type="url"
            value={avatar}
            onChange={(e) => { setAvatar(e.target.value); setProfileSuccess(false); }}
            placeholder="https://example.com/avatar.jpg"
            className={INPUT_BASE}
          />
        </FormField>

        <InlineError error={profileError} onDismiss={() => setProfileError(null)} />

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={updatingProfile}
            className={SUBMIT_BUTTON}
          >
            {updatingProfile && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Profile
          </button>
          {profileSuccess && <span className="text-sm text-green-400">Profile updated</span>}
        </div>
      </form>

      {/* Password */}
      <div className="border-t border-[var(--color-border)] pt-5">
        <button
          type="button"
          onClick={() => setShowPasswordSection(!showPasswordSection)}
          className="flex items-center gap-2 text-sm font-medium text-[var(--color-text-muted)] hover:text-white transition-colors"
        >
          <Key className="w-4 h-4" />
          {showPasswordSection ? 'Hide password change' : 'Change password'}
        </button>

        {showPasswordSection && (
          <form onSubmit={handlePasswordChange} className="mt-4 space-y-4">
            <FormField label="Current Password" htmlFor="pm-curpass">
              <input
                id="pm-curpass"
                type="password"
                required
                value={currentPassword}
                onChange={(e) => { setCurrentPassword(e.target.value); setPasswordError(null); }}
                className={INPUT_BASE}
              />
            </FormField>

            <FormField label="New Password" htmlFor="pm-newpass">
              <input
                id="pm-newpass"
                type="password"
                required
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setPasswordError(null); }}
                className={INPUT_BASE}
              />
            </FormField>

            <FormField label="Confirm New Password" htmlFor="pm-confpass">
              <input
                id="pm-confpass"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setPasswordError(null); }}
                className={INPUT_BASE}
              />
            </FormField>

            <InlineError error={passwordError} onDismiss={() => setPasswordError(null)} />

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={changingPassword}
                className={SUBMIT_BUTTON}
              >
                {changingPassword && <Loader2 className="w-4 h-4 animate-spin" />}
                Change Password
              </button>
              {passwordSuccess && <span className="text-sm text-green-400">Password changed</span>}
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}
